import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, GraduationCap, Users, Download } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Navbar } from "@/components/Navbar";
import { MaskLines } from "@/components/Motion";
import { AdminTabs } from "@/components/admin/AdminTabs";
import { ClassDialog } from "@/components/admin/ClassDialog";
import { BulkCards } from "@/components/admin/BulkCards";
import { api, errMsg } from "@/lib/api";
import { downloadCardsPdf, downloadSectionsPdf, downloadSectionsZip } from "@/lib/loginCards";
import { useLang } from "@/context/LangContext";

export default function AdminClasses() {
  const [classes, setClasses] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cardsFor, setCardsFor] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const { t, dir } = useLang();

  // Fresh passwords are issued, so the admin confirms in a dialog first.
  // cardsFor: { key, title, count?, fetch: () => Promise<pdf> }
  const downloadCards = async () => {
    const job = cardsFor;
    setCardsFor(null);
    setBusyId(job.key);
    const id = toast.loading("جارٍ تجهيز البطاقات...");
    try {
      await job.run();
      toast.success(`تم تنزيل بطاقات «${job.title}»`);
    } catch (e) { toast.error(e?.response ? errMsg(e) : e?.message || errMsg(e)); } finally { toast.dismiss(id); setBusyId(null); }
  };

  const classJob = (c) => ({
    key: c.id, title: c.name, count: c.students?.length ?? 0,
    run: async () => { const { data } = await api.post(`/admin/classes/${c.id}/cards`); await downloadCardsPdf(data, t.teacher, dir || "rtl"); },
  });

  const bulkJob = (scope, value, title) => scope === "excel" ? {
    key: `excel:${value}`, title,
    run: async () => {
      const res = await api.post("/admin/school-logins", { school: value }, { responseType: "blob" }).catch(async (e) => {
        const text = await e?.response?.data?.text?.().catch(() => "");
        let msg = "";
        try { msg = JSON.parse(text)?.detail; } catch { msg = ""; }
        throw new Error(typeof msg === "string" && msg ? msg : errMsg(e));
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `${value.replace(/[\\/:*?"<>|]/g, "-")}-أسماء-الدخول.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    },
  } : scope === "teachers" ? {
    key: `teachers:${value}`, title,
    run: async () => {
      const { data } = await api.post("/admin/teacher-cards", { school: value });
      const tt = { ...t.teacher, cardTitle: t.teacher.teacherCardTitle, cardStudent: t.teacher.cardTeacher, cardClass: t.teacher.cardSchool };
      await downloadCardsPdf(data, tt, dir || "rtl", title);
    },
  } : ({
    key: `${scope}:${value}`, title,
    run: async () => {
      const { data } = await api.post("/admin/cards", { scope, value });
      const sections = (Array.isArray(data?.sections) ? data.sections : []).map((s) => (s.no_class ? { ...s, class_name: t.teacher.noClassSection } : s));
      if (!sections.length) throw new Error("لا يوجد طلاب لتنزيل بطاقاتهم");
      const save = scope === "school" ? downloadSectionsZip : downloadSectionsPdf;
      await save({ sections }, t.teacher, dir || "rtl", title);
    },
  });

  const load = useCallback(() => {
    api.get("/admin/classes").then((r) => setClasses(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/users").then((r) => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (dialog.item) await api.put(`/admin/classes/${dialog.item.id}`, payload);
      else await api.post("/admin/classes", payload);
      toast.success(dialog.item ? "تم حفظ الصف" : "تم إنشاء الصف");
      setDialog({ open: false, item: null });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/classes/${toDelete.id}`);
      toast.success(`تم حذف «${toDelete.name}»`);
      setToDelete(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]" dir="rtl" data-testid="admin-classes-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <p className="text-sm font-semibold text-[#B45309] mb-2">لوحة الإدارة</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold"><MaskLines lines={["إدارة الصفوف"]} delay={0.1} /></h1>
          </div>
          <button onClick={() => setDialog({ open: true, item: null })} data-testid="admin-create-class-trigger"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#8B3A2B] text-[#FAF7F2] px-6 py-3 font-semibold hover:bg-[#702E22] transition-colors">
            <Plus className="w-5 h-5" /> إنشاء صف
          </button>
        </div>
        <AdminTabs />

        <BulkCards users={users} busy={Boolean(busyId)} onPick={(scope, value, title) => setCardsFor(bulkJob(scope, value, title))} />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-classes-grid">
          {classes.map((c) => (
            <article key={c.id} className="bg-white border border-[#E5DCCB] p-6 flex flex-col gap-4" data-testid={`admin-class-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-display text-2xl font-bold">{c.name}</h2>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setDialog({ open: true, item: c })} data-testid={`admin-edit-class-btn-${c.id}`} className="p-2 rounded-full hover:bg-[#F2ECE1]" aria-label="تعديل"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => setToDelete(c)} data-testid={`admin-delete-class-btn-${c.id}`} className="p-2 rounded-full text-red-800 hover:bg-red-50" aria-label="حذف"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="flex items-center gap-2 text-sm"><GraduationCap className="w-4 h-4 text-[#047857]" /> {c.teacher_name || <span className="text-amber-800">بدون معلم</span>}</p>
              <p className="flex items-center gap-2 text-sm text-stone-700"><Users className="w-4 h-4 text-[#8B3A2B]" /> {c.students?.length ?? 0} طالب</p>
              <div className="flex flex-wrap gap-1.5">
                {(c.students || []).slice(0, 8).map((s) => <span key={s.id} className="text-xs bg-[#F2ECE1] rounded-full px-2.5 py-1">{s.name}</span>)}
                {(c.students?.length ?? 0) > 8 && <span className="text-xs text-stone-600 px-2 py-1">+{c.students.length - 8}</span>}
              </div>
              <button onClick={() => setCardsFor(classJob(c))} disabled={!c.students?.length || busyId === c.id} data-testid={`admin-class-cards-btn-${c.id}`}
                className="mt-auto inline-flex items-center justify-center gap-2 rounded-full border border-[#1C1917] px-5 py-2.5 text-sm font-semibold hover:bg-[#1C1917] hover:text-white transition-colors disabled:opacity-40">
                <Download className="w-4 h-4" /> تنزيل بطاقات الصف
              </button>
            </article>
          ))}
        </div>
        {!classes.length && <p className="p-8 text-center text-stone-600 bg-white border border-[#E5DCCB]" data-testid="admin-classes-empty">لا توجد صفوف بعد. اضغط «إنشاء صف» واختر المعلم والطلاب.</p>}
      </div>

      <ClassDialog open={dialog.open} initial={dialog.item} users={users} saving={saving} onSubmit={save} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent dir="rtl" className="bg-[#FAF7F2]" data-testid="admin-delete-class-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right font-display text-2xl">حذف الصف؟</AlertDialogTitle>
            <AlertDialogDescription className="text-right">سيُحذف «{toDelete?.name}» ومهام القراءة الخاصة به. حسابات الطلاب والمعلم لن تُحذف.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel data-testid="admin-delete-class-cancel">إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-800 hover:bg-red-900" data-testid="admin-delete-class-confirm">حذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(cardsFor)} onOpenChange={(o) => !o && setCardsFor(null)}>
        <AlertDialogContent dir="rtl" className="bg-[#FAF7F2]" data-testid="admin-class-cards-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right font-display text-2xl">تنزيل بطاقات «{cardsFor?.title}»؟</AlertDialogTitle>
            <AlertDialogDescription className="text-right">ستُنشأ كلمات مرور جديدة {cardsFor?.count != null ? `لـ${cardsFor.count} طالب` : cardsFor?.key?.startsWith("teachers:") ? "لمعلمي هذه المدرسة" : cardsFor?.key?.startsWith("excel:") ? "لكل طلاب هذه المدرسة (ملف Excel)" : "لكل الطلاب المشمولين"}، ولن تعمل كلماتهم القديمة. شارك الملف مع المعلم أو المدرسة لتوزيع البطاقات.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel data-testid="admin-class-cards-cancel">إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={downloadCards} className="bg-[#8B3A2B] hover:bg-[#702E22]" data-testid="admin-class-cards-confirm">تنزيل البطاقات</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
